import type { MediaLibraryAccessUrl, MediaLibraryEntry } from '@refly/openapi-schema';

type ApiResponse<T> = {
  success?: boolean;
  data?: T;
  message?: string;
  errMsg?: string;
};

type MediaKind = NonNullable<MediaLibraryEntry['mediaKind']>;

export type ListMediaLibraryFilesOptions = {
  mediaKinds?: MediaKind[];
  fetchImpl?: typeof fetch;
  maxConcurrency?: number;
  signal?: AbortSignal;
};

const readResponse = async <T>(response: Response, fallback: string): Promise<T> => {
  const payload = (await response.json()) as ApiResponse<T>;
  if (!response.ok || !payload.success || payload.data === undefined) {
    throw new Error(payload.message || payload.errMsg || fallback);
  }
  return payload.data;
};

const compareEntries = (left: MediaLibraryEntry, right: MediaLibraryEntry) => {
  const leftPath = `${left.path}/${left.name}`;
  const rightPath = `${right.path}/${right.name}`;
  if (leftPath < rightPath) return -1;
  if (leftPath > rightPath) return 1;
  return left.entryId.localeCompare(right.entryId);
};

export async function listMediaLibraryFiles(
  options: ListMediaLibraryFilesOptions = {},
): Promise<MediaLibraryEntry[]> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const allowedKinds = options.mediaKinds?.length ? new Set(options.mediaKinds) : null;
  const maxConcurrency = Math.max(1, Math.min(10, options.maxConcurrency ?? 4));
  const pending: Array<string | null> = [null];
  const visitedFolders = new Set<string>();
  const visitedEntries = new Set<string>();
  const files: MediaLibraryEntry[] = [];

  while (pending.length > 0) {
    const batch = pending.splice(0, maxConcurrency);
    const directories = await Promise.all(
      batch.map(async (parentId) => {
        const query = parentId ? `?parentId=${encodeURIComponent(parentId)}` : '';
        const response = await fetchImpl(`/v1/media-library/entries${query}`, {
          credentials: 'include',
          signal: options.signal,
        });
        return readResponse<MediaLibraryEntry[]>(response, '获取素材列表失败');
      }),
    );

    for (const entries of directories) {
      for (const entry of entries) {
        if (visitedEntries.has(entry.entryId)) continue;
        visitedEntries.add(entry.entryId);
        if (entry.type === 'folder') {
          if (!visitedFolders.has(entry.entryId)) {
            visitedFolders.add(entry.entryId);
            pending.push(entry.entryId);
          }
          continue;
        }
        if (!allowedKinds || (entry.mediaKind && allowedKinds.has(entry.mediaKind))) {
          files.push(entry);
        }
      }
    }
  }

  return files.sort(compareEntries);
}

export async function getMediaLibraryEntryAccessUrl(
  entryId: string,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<MediaLibraryAccessUrl> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const response = await fetchImpl(
    `/v1/media-library/entries/${encodeURIComponent(entryId)}/access-url`,
    { credentials: 'include', signal: options.signal },
  );
  return readResponse<MediaLibraryAccessUrl>(response, '获取素材访问地址失败');
}
